/**
 * Notification Service
 * Production-ready service for managing notifications in Firebase
 */

import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Notification,
  NotificationFormData,
  NotificationType,
  NotificationPriority,
  NotificationStats,
  NotificationFilter,
} from '../types/notification';
import { logger } from './loggerService';

const COLLECTION_NAME = 'notifications';

// Helper function to convert Firestore Timestamp to Date
const convertTimestamp = (timestamp: any): Date => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date(timestamp);
};

// Helper function to convert Date to Firestore Timestamp
const convertToTimestamp = (date: Date) => {
  return Timestamp.fromDate(date);
};

class NotificationService {
  /**
   * Get all notifications with optional filtering
   */
  async getNotifications(filter?: NotificationFilter): Promise<Notification[]> {
    try {
      const notificationsRef = collection(db, COLLECTION_NAME);
      let q = query(notificationsRef, orderBy('createdAt', 'desc'));

      // Apply filters
      if (filter?.type) {
        q = query(q, where('type', '==', filter.type));
      }
      if (filter?.priority) {
        q = query(q, where('priority', '==', filter.priority));
      }
      if (filter?.isRead !== undefined) {
        q = query(q, where('isRead', '==', filter.isRead));
      }

      const snapshot = await getDocs(q);
      const notifications: Notification[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          type: data.type,
          priority: data.priority,
          title: data.title,
          message: data.message,
          isRead: data.isRead,
          createdAt: convertTimestamp(data.createdAt),
          readAt: data.readAt ? convertTimestamp(data.readAt) : undefined,
          relatedEntityId: data.relatedEntityId,
          relatedEntityType: data.relatedEntityType,
          metadata: data.metadata,
          actionUrl: data.actionUrl,
          actionLabel: data.actionLabel,
        };
      });

      logger.debug('Notifications fetched', { count: notifications.length, filter });
      return notifications;
    } catch (error) {
      logger.error('Error fetching notifications', error, {
        component: 'NotificationService',
        action: 'getNotifications',
      });
      throw error;
    }
  }

  /**
   * Get unread notifications count
   */
  async getUnreadCount(): Promise<number> {
    try {
      const notificationsRef = collection(db, COLLECTION_NAME);
      const q = query(notificationsRef, where('isRead', '==', false));
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      logger.error('Error getting unread count', error, {
        component: 'NotificationService',
        action: 'getUnreadCount',
      });
      return 0;
    }
  }

  /**
   * Get notification statistics
   */
  async getStats(): Promise<NotificationStats> {
    try {
      const notifications = await this.getNotifications();
      
      const stats: NotificationStats = {
        total: notifications.length,
        unread: notifications.filter((n) => !n.isRead).length,
        byType: {} as Record<NotificationType, number>,
        byPriority: {} as Record<NotificationPriority, number>,
      };

      // Count by type
      notifications.forEach((notification) => {
        stats.byType[notification.type] = (stats.byType[notification.type] || 0) + 1;
        stats.byPriority[notification.priority] = (stats.byPriority[notification.priority] || 0) + 1;
      });

      return stats;
    } catch (error) {
      logger.error('Error getting notification stats', error, {
        component: 'NotificationService',
        action: 'getStats',
      });
      throw error;
    }
  }

  /**
   * Get a single notification by ID
   */
  async getNotification(id: string): Promise<Notification | null> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return {
        id: docSnap.id,
        type: data.type,
        priority: data.priority,
        title: data.title,
        message: data.message,
        isRead: data.isRead,
        createdAt: convertTimestamp(data.createdAt),
        readAt: data.readAt ? convertTimestamp(data.readAt) : undefined,
        relatedEntityId: data.relatedEntityId,
        relatedEntityType: data.relatedEntityType,
        metadata: data.metadata,
        actionUrl: data.actionUrl,
        actionLabel: data.actionLabel,
      };
    } catch (error) {
      logger.error('Error fetching notification', error, {
        component: 'NotificationService',
        action: 'getNotification',
        data: { id },
      });
      throw error;
    }
  }

  /**
   * Create a new notification
   */
  async createNotification(data: NotificationFormData): Promise<string> {
    try {
      const notificationData = {
        ...data,
        isRead: false,
        createdAt: convertToTimestamp(new Date()),
      };

      const docRef = await addDoc(collection(db, COLLECTION_NAME), notificationData);
      
      logger.info('Notification created', {
        notificationId: docRef.id,
        type: data.type,
        priority: data.priority,
      });

      return docRef.id;
    } catch (error) {
      logger.error('Error creating notification', error, {
        component: 'NotificationService',
        action: 'createNotification',
        data,
      });
      throw error;
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(id: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(docRef, {
        isRead: true,
        readAt: convertToTimestamp(new Date()),
      });

      logger.info('Notification marked as read', { notificationId: id });
    } catch (error) {
      logger.error('Error marking notification as read', error, {
        component: 'NotificationService',
        action: 'markAsRead',
        data: { id },
      });
      throw error;
    }
  }

  /**
   * Mark multiple notifications as read
   */
  async markMultipleAsRead(ids: string[]): Promise<void> {
    try {
      const batch = writeBatch(db);
      const readAt = convertToTimestamp(new Date());

      ids.forEach((id) => {
        const docRef = doc(db, COLLECTION_NAME, id);
        batch.update(docRef, { isRead: true, readAt });
      });

      await batch.commit();
      
      logger.info('Multiple notifications marked as read', { count: ids.length });
    } catch (error) {
      logger.error('Error marking multiple notifications as read', error, {
        component: 'NotificationService',
        action: 'markMultipleAsRead',
        data: { count: ids.length },
      });
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    try {
      const unreadNotifications = await this.getNotifications({ isRead: false });
      const ids = unreadNotifications.map((n) => n.id);
      
      if (ids.length > 0) {
        await this.markMultipleAsRead(ids);
      }

      logger.info('All notifications marked as read', { count: ids.length });
    } catch (error) {
      logger.error('Error marking all notifications as read', error, {
        component: 'NotificationService',
        action: 'markAllAsRead',
      });
      throw error;
    }
  }

  /**
   * Delete a notification
   */
  async deleteNotification(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTION_NAME, id));
      logger.info('Notification deleted', { notificationId: id });
    } catch (error) {
      logger.error('Error deleting notification', error, {
        component: 'NotificationService',
        action: 'deleteNotification',
        data: { id },
      });
      throw error;
    }
  }

  /**
   * Delete multiple notifications
   */
  async deleteMultiple(ids: string[]): Promise<void> {
    try {
      const batch = writeBatch(db);

      ids.forEach((id) => {
        const docRef = doc(db, COLLECTION_NAME, id);
        batch.delete(docRef);
      });

      await batch.commit();
      
      logger.info('Multiple notifications deleted', { count: ids.length });
    } catch (error) {
      logger.error('Error deleting multiple notifications', error, {
        component: 'NotificationService',
        action: 'deleteMultiple',
        data: { count: ids.length },
      });
      throw error;
    }
  }

  /**
   * Delete all read notifications
   */
  async deleteAllRead(): Promise<void> {
    try {
      const readNotifications = await this.getNotifications({ isRead: true });
      const ids = readNotifications.map((n) => n.id);
      
      if (ids.length > 0) {
        await this.deleteMultiple(ids);
      }

      logger.info('All read notifications deleted', { count: ids.length });
    } catch (error) {
      logger.error('Error deleting all read notifications', error, {
        component: 'NotificationService',
        action: 'deleteAllRead',
      });
      throw error;
    }
  }

  /**
   * Subscribe to real-time notification updates
   * Returns unsubscribe function
   */
  subscribeToNotifications(
    callback: (notifications: Notification[]) => void,
    filter?: NotificationFilter
  ): () => void {
    try {
      const notificationsRef = collection(db, COLLECTION_NAME);
      let q = query(notificationsRef, orderBy('createdAt', 'desc'), limit(50));

      // Apply filters
      if (filter?.isRead !== undefined) {
        q = query(q, where('isRead', '==', filter.isRead));
      }

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const notifications: Notification[] = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              type: data.type,
              priority: data.priority,
              title: data.title,
              message: data.message,
              isRead: data.isRead,
              createdAt: convertTimestamp(data.createdAt),
              readAt: data.readAt ? convertTimestamp(data.readAt) : undefined,
              relatedEntityId: data.relatedEntityId,
              relatedEntityType: data.relatedEntityType,
              metadata: data.metadata,
              actionUrl: data.actionUrl,
              actionLabel: data.actionLabel,
            };
          });

          callback(notifications);
          logger.debug('Notifications real-time update', { count: notifications.length });
        },
        (error) => {
          logger.error('Error in notification subscription', error, {
            component: 'NotificationService',
            action: 'subscribeToNotifications',
          });
        }
      );

      return unsubscribe;
    } catch (error) {
      logger.error('Error subscribing to notifications', error, {
        component: 'NotificationService',
        action: 'subscribeToNotifications',
      });
      return () => {};
    }
  }

  /**
   * Helper method to create order notification
   */
  async createOrderNotification(
    orderId: string,
    orderNumber: string,
    type: 'new' | 'update' | 'cancelled',
    customerName?: string
  ): Promise<string> {
    const typeMap = {
      new: NotificationType.ORDER_NEW,
      update: NotificationType.ORDER_UPDATE,
      cancelled: NotificationType.ORDER_CANCELLED,
    };

    const titleMap = {
      new: 'New Order Received',
      update: 'Order Updated',
      cancelled: 'Order Cancelled',
    };

    const messageMap = {
      new: `New order #${orderNumber}${customerName ? ` from ${customerName}` : ''} has been placed.`,
      update: `Order #${orderNumber} has been updated.`,
      cancelled: `Order #${orderNumber} has been cancelled.`,
    };

    return await this.createNotification({
      type: typeMap[type],
      priority: type === 'new' ? NotificationPriority.HIGH : NotificationPriority.MEDIUM,
      title: titleMap[type],
      message: messageMap[type],
      relatedEntityId: orderId,
      relatedEntityType: 'order',
      actionUrl: `/orders`,
      actionLabel: 'View Orders',
    });
  }

  /**
   * Helper method to create product stock notification
   */
  async createStockNotification(
    productId: string,
    productName: string,
    stockLevel: number
  ): Promise<string> {
    const isOutOfStock = stockLevel === 0;

    return await this.createNotification({
      type: isOutOfStock ? NotificationType.PRODUCT_OUT_OF_STOCK : NotificationType.PRODUCT_LOW_STOCK,
      priority: isOutOfStock ? NotificationPriority.URGENT : NotificationPriority.HIGH,
      title: isOutOfStock ? 'Product Out of Stock' : 'Low Stock Alert',
      message: isOutOfStock
        ? `${productName} is out of stock.`
        : `${productName} is running low (${stockLevel} units remaining).`,
      relatedEntityId: productId,
      relatedEntityType: 'product',
      actionUrl: `/products`,
      actionLabel: 'View Products',
      metadata: { stockLevel },
    });
  }
}

export const notificationService = new NotificationService();
